import { Model } from '../../../../types/model'
import {
  AlertError,
  PrimaryAction,
  SupportingText,
  Text,
} from '@ohgoodlord/design-system'
import { OverlayComponentProps } from '../../types'
import { DefaultFooter } from '../default-footer'
import { DefaultHeader } from '../default-header'
import { useForm } from 'react-hook-form'
import {
  Asterix,
  FormWrapper,
  InputLabel,
  InputWrapper,
  SelectWrapper,
  StyledContainer,
  StyledDescription,
} from './styles'
import {
  Currency,
  DEFAULT_GBP_CURRENCY,
  getCurrencyDetailsConfirmationMessage,
  resolveCurrency,
} from './utils'

export const CURRENCY_DETAILS = 'currency-details' as const

export interface CurrencyDetailsOverlayModel extends Model<typeof CURRENCY_DETAILS> {
  overlayTitle: string
  message?: string
  source?: Currency[]
  common?: string[]
  incomeType?: string
  frequency?: string[]
  frequencyLabel?: string
  valueLabel?: string
}

interface FormValues {
  value: string
  currencyCode: string
  frequency: string
}

export type Props = OverlayComponentProps<CurrencyDetailsOverlayModel>

export const CurrencyDetailsOverlay = ({
  model,
  isMobile,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  onClose,
}: Props) => {
  const isSavings = model.incomeType === 'Savings'
  const showCurrencySelect = Boolean(model.source?.length)
  const showFrequency = !isSavings
  const frequencyOptions = Array.isArray(model.frequency) ? model.frequency : undefined
  const commonCodes = model.common ?? ['GBP']
  const valueLabel = model.valueLabel ?? 'Amount'
  const frequencyLabel = model.frequencyLabel ?? 'Frequency'
  const valueErrorMessage = `Please enter the ${model.valueLabel ?? 'amount'}.`
  const frequencyErrorMessage = `Please enter the ${model.frequencyLabel ?? 'frequency'}.`

  const commonCurrencies = (model.source ?? []).filter(currency =>
    commonCodes.includes(currency.code),
  )
  const uncommonCurrencies = (model.source ?? []).filter(
    currency => !commonCodes.includes(currency.code),
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      value: '',
      currencyCode: showCurrencySelect ? '' : DEFAULT_GBP_CURRENCY.code,
      frequency: '',
    },
  })

  const onSubmit = (formValues: FormValues) => {
    const currency = resolveCurrency(formValues.currencyCode, model.source)
    const payload = {
      value: formValues.value,
      currency,
      frequency: formValues.frequency,
      COMMON_CURRENCIES: commonCodes,
    }

    onSendMessage({
      message: getCurrencyDetailsConfirmationMessage(payload),
    })
    onSendEvent({
      name: 'form',
      value: payload,
    })
    onOverlaySubmit()
  }

  return (
    <FormWrapper onSubmit={handleSubmit(onSubmit)}>
      <StyledContainer>
        {renderOverlayHeader !== undefined ? (
          renderOverlayHeader()
        ) : (
          <DefaultHeader
            modal={model.modal}
            isMobile={isMobile}
            title={model.overlayTitle}
            onClose={onClose}
          />
        )}
        <StyledDescription>
          {model.message && <SupportingText align="left">{model.message}</SupportingText>}

          {showCurrencySelect && (
            <SelectWrapper>
              <InputLabel htmlFor="currency-selector" $hasAsterix>
                <SupportingText align="left">Currency</SupportingText>
                <Asterix>*</Asterix>
              </InputLabel>
              <select
                id="currency-selector"
                data-testid="currency-selector"
                {...register('currencyCode', { required: true })}
              >
                <option disabled value="">
                  Currency
                </option>
                {commonCurrencies.map(currency => (
                  <option key={`common-${currency.code}`} value={currency.code}>
                    {currency.symbol} - {currency.name}
                  </option>
                ))}
                <option disabled value="-">
                  -
                </option>
                {uncommonCurrencies.map(currency => (
                  <option key={`uncommon-${currency.code}`} value={currency.code}>
                    {currency.symbol} - {currency.name}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          )}
          {errors.currencyCode !== undefined && (
            <AlertError>
              <Text>Please select a currency.</Text>
            </AlertError>
          )}

          <InputWrapper>
            <InputLabel htmlFor="value" $hasAsterix>
              <SupportingText align="left">{valueLabel}</SupportingText>
              <Asterix>*</Asterix>
            </InputLabel>
            <input
              id="value"
              type="number"
              step="any"
              {...register('value', { required: true })}
            />
          </InputWrapper>
          {errors.value !== undefined && (
            <AlertError>
              <Text>{valueErrorMessage}</Text>
            </AlertError>
          )}

          {showFrequency && frequencyOptions && (
            <SelectWrapper>
              <InputLabel htmlFor="frequency-selector" $hasAsterix>
                <SupportingText align="left">{frequencyLabel}</SupportingText>
                <Asterix>*</Asterix>
              </InputLabel>
              <select
                id="frequency-selector"
                data-testid="frequency-selector"
                {...register('frequency', { required: true })}
              >
                <option disabled value="">
                  {frequencyLabel}
                </option>
                {frequencyOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          )}

          {showFrequency && !frequencyOptions && (
            <InputWrapper>
              <InputLabel htmlFor="frequency" $hasAsterix>
                <SupportingText align="left">{frequencyLabel}</SupportingText>
                <Asterix>*</Asterix>
              </InputLabel>
              <input
                id="frequency"
                type="number"
                {...register('frequency', { required: true })}
              />
            </InputWrapper>
          )}
          {errors.frequency !== undefined && (
            <AlertError>
              <Text>{frequencyErrorMessage}</Text>
            </AlertError>
          )}
        </StyledDescription>
      </StyledContainer>
      <DefaultFooter
        renderActions={() => (
          <PrimaryAction type="submit" isFullWidth testId="modal-primary-action-button">
            Done
          </PrimaryAction>
        )}
      />
    </FormWrapper>
  )
}
